import {
  createContext,
  useContext,
  useRef,
  type ReactNode,
  createRef,
} from 'react'
import { useTiles } from '../../hooks/useTiles'
import { MATERIAL_TYPES } from '@mixtiles/web-backend-shared'
import type { Tile } from 'components/TilesProvider/TilesProvider.types'

interface TileRef {
  current: HTMLElement | null
}

interface TileRefs {
  [key: string]: React.RefObject<HTMLElement | null>
}

interface TilesContextValue {
  tileRefs: React.MutableRefObject<TileRefs>
  highlightedTileRef: React.MutableRefObject<TileRef | undefined>
  getRef: (params: {
    isHighlightedTile: boolean
    tileId: string
  }) =>
    | React.RefObject<HTMLElement | null>
    | React.MutableRefObject<TileRef | undefined>
  undoTiles: () => void
  saveTilesState: () => void
  tileMaterialToLastMaterialState: React.MutableRefObject<
    Record<MATERIAL_TYPES, MATERIAL_TYPES>
  >
  setTileMaterialToActualMaterial: (currentMaterial: MATERIAL_TYPES) => void
}

const TilesRefsContext = createContext<TilesContextValue | undefined>(undefined)

interface TilesRefsProviderProps {
  children: ReactNode
}

export function TilesRefsProvider({ children }: TilesRefsProviderProps) {
  const tileRefs = useRef<TileRefs>({})
  const highlightedTileRef = useRef<TileRef | undefined>(undefined)
  const previousTiles = useRef<Tile[]>([])
  const { tiles, setTiles } = useTiles()
  const tileMaterialToLastMaterialState = useRef<
    Record<MATERIAL_TYPES, MATERIAL_TYPES>
  >({
    [MATERIAL_TYPES.CLASSIC]: MATERIAL_TYPES.CLASSIC,
    [MATERIAL_TYPES.WIDE_FRAME]: MATERIAL_TYPES.WIDE_FRAME,
    [MATERIAL_TYPES.CANVAS]: MATERIAL_TYPES.CANVAS,
    [MATERIAL_TYPES.STICKER]: MATERIAL_TYPES.STICKER,
    [MATERIAL_TYPES.FRAMELESS]: MATERIAL_TYPES.FRAMELESS,
  })

  const setTileMaterialToActualMaterial = (currentMaterial: MATERIAL_TYPES) => {
    if (currentMaterial === MATERIAL_TYPES.WIDE_FRAME) {
      tileMaterialToLastMaterialState.current[MATERIAL_TYPES.CLASSIC] =
        currentMaterial
    } else {
      tileMaterialToLastMaterialState.current[currentMaterial] = currentMaterial
    }
  }

  const getRef = ({
    isHighlightedTile,
    tileId,
  }: {
    isHighlightedTile: boolean
    tileId: string
  }) => {
    if (!tileRefs.current[tileId]) {
      tileRefs.current[tileId] = createRef<HTMLElement>()
    }

    if (isHighlightedTile) {
      return highlightedTileRef
    }

    return tileRefs.current[tileId]
  }

  const saveTilesState = () => {
    previousTiles.current = [...tiles.map((tile: Tile) => ({ ...tile }))]
  }

  const undoTiles = () => {
    setTiles([...previousTiles.current])
    previousTiles.current = []
  }

  return (
    <TilesRefsContext.Provider
      value={{
        tileRefs,
        highlightedTileRef,
        getRef,
        undoTiles,
        saveTilesState,
        tileMaterialToLastMaterialState,
        setTileMaterialToActualMaterial,
      }}
    >
      {children}
    </TilesRefsContext.Provider>
  )
}

export function useTilesRefs(): TilesContextValue {
  const context = useContext(TilesRefsContext)

  if (context === undefined) {
    throw new Error('useTilesRefs must be used within a TilesRefsProvider')
  }

  return context
}
